/**
 * Emit hosts for the `template` analytics group — 13 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@heidi/analytics/events';
import { type TemplateVisibility } from '@heidi/schema/ml-scribe';

export function trackPDFTemplateCreated({
  templateId,
  templateName,
  teamId,
  sectionCount,
}: {
  templateId: string;
  templateName: string;
  teamId?: string | null;
  sectionCount?: number;
}) {
  track({
    event_name: TrackingEventName.TemplateCreated,
    props: {
      template_id: templateId,
      template_name: templateName,
      team_id: teamId ?? undefined,
      src: 'pdf',
      section_count: sectionCount,
    },
  });
}

export function trackTemplateDeleted(templateId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateDeleted,
      templateId,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateDeleted,
    props: {
      template_id: templateId,
      delete_action: 'template_deleted',
    },
  });
}

export function trackTemplateEdited({
  templateId,
  templateVisibility,
}: {
  templateId: string;
  templateVisibility: TemplateVisibility;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateEdited,
      templateId,
      templateVisibility,
    },
  });
}

export function trackTemplateFavourited({
  templateId,
  templateType,
  templateFavourited,
}: {
  templateId: string;
  templateType: TemplateVisibility;
  templateFavourited: boolean;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateFavourited,
      templateId,
      templateType,
      templateFavourited,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_id: templateId,
      update_action: 'favourited',
      template_type: templateType,
      is_favorite: templateFavourited,
    },
  });
}

export function trackTemplateLibraryViewed() {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateLibraryViewed,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateViewed,
    props: {},
  });
}

export function trackTemplateUpdated({
  templateId,
  templateName,
  teamId,
  version,
  sectionCount,
  templateType,
  useSections,
}: {
  templateId?: string;
  templateName?: string;
  teamId?: string;
  version: 'v1' | 'v2';
  sectionCount?: number;
  /**
   * Template category of the saved template. Without it a patient-summary
   * template edit is indistinguishable from any other save, since
   * `template_name` is clinician-authored.
   */
  templateType?: string;
  useSections?: boolean;
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_id: templateId,
      template_name: templateName,
      team_id: teamId,
      template_editor_version: version,
      section_count: sectionCount,
      template_type: templateType,
      use_sections: useSections,
    },
  });
}
